import { Extension } from "@tiptap/core";
import { Selection } from "@tiptap/pm/state";
import { isInTable, selectedRect } from "@tiptap/pm/tables";

/**
 * The Word shortcuts arbitrators reach for, on top of what Tiptap binds
 * already. Already free, so NOT bound here: Ctrl+B/I/U, Ctrl+Z, Ctrl+Y and
 * Ctrl+Shift+Z (UndoRedo binds Mod-y too), Tab / Shift+Tab between table
 * cells, Tab in lists, and Ctrl+Enter (PageBreak.js).
 *
 * `Mod-` is Cmd on macOS and Ctrl elsewhere - do not add separate bindings.
 *
 * Every handler that claims a key returns true, which is what makes
 * ProseMirror call preventDefault. Several of these keys are browser
 * shortcuts, and a false return hands the keystroke straight back to the
 * browser: Ctrl+S opens "Save page as", Ctrl+R reloads the SPA (and the
 * unsaved draft with it), Ctrl+L and Ctrl+E move focus to the address bar,
 * Ctrl+J opens downloads, Alt+Home goes to the home page in Firefox.
 */

// Word's table autoformat: a line like +----+--------+ then Enter becomes a
// one-row table, each column as wide as its run of dashes.
const RULER = /^\+(?:-+\+)+$/;

const inList = (editor) =>
  editor.isActive("bulletList") || editor.isActive("orderedList");

// The row's cells all start and end in that row - no rowspan reaches into it
// from above, and none of its own cells reaches below it.
function isSelfContainedRow(map, row) {
  for (let col = 0; col < map.width; col += 1) {
    const cell = map.findCell(map.map[row * map.width + col]);
    if (cell.top !== row || cell.bottom !== row + 1) return false;
  }
  return true;
}

export const WordShortcuts = Extension.create({
  name: "wordShortcuts",

  // Above StarterKit's default of 100, so a key bound in both goes here first.
  // The one that matters is Mod-e: Code binds it to inline code, which nothing
  // in this app uses and bac/html_docx.py does not render, and Word users
  // press it to centre a line.
  priority: 200,

  addOptions() {
    return {
      // (html) => void. Ctrl+S never reaches the browser, handler or not.
      onSave: null,
      // (html) => void. Return false to let the browser's own print dialog
      // open instead; with no handler at all, it opens anyway.
      onPrint: null,
    };
  },

  addKeyboardShortcuts() {
    const { editor } = this;

    const align = (value) => () => {
      if (!editor.commands.setTextAlign) return false;
      editor.commands.setTextAlign(value);
      return true;
    };

    // Same split as the indent/outdent toolbar buttons: list nesting inside a
    // list, margin-left outside it.
    const indent = (deeper) => () => {
      if (inList(editor)) {
        if (deeper) editor.commands.sinkListItem("listItem");
        else editor.commands.liftListItem("listItem");
      } else if (deeper) {
        editor.commands.indent();
      } else {
        editor.commands.outdent();
      }
      return true;
    };

    // Move the caret to the start of another cell. `pick` gets the current
    // cell's rect (plus the table map) and returns the [row, col] to go to.
    const goToCell = (pick) => () => {
      const { state } = editor;
      if (!isInTable(state)) return false;
      const rect = selectedRect(state);
      const [row, col] = pick(rect, rect.map);
      const cellPos = rect.tableStart + rect.map.map[row * rect.map.width + col];
      return editor
        .chain()
        .command(({ tr }) => {
          tr.setSelection(Selection.near(tr.doc.resolve(cellPos + 1)));
          return true;
        })
        .scrollIntoView()
        .run();
    };

    // Swap the caret's row with its neighbour. Rows inside a vertical merge
    // stay put - moving one would tear the merged cell in two.
    const moveRow = (direction) => () => {
      const { state } = editor;
      if (!isInTable(state)) return false;
      const { top, bottom, map, table, tableStart } = selectedRect(state);
      const target = top + direction;
      if (
        bottom - top !== 1 ||
        target < 0 ||
        target >= map.height ||
        !isSelfContainedRow(map, top) ||
        !isSelfContainedRow(map, target)
      ) {
        // Still claimed: Alt+Shift+Arrow would otherwise extend the selection.
        return true;
      }

      const upper = Math.min(top, target);
      let upperStart = tableStart;
      for (let i = 0; i < upper; i += 1) upperStart += table.child(i).nodeSize;
      const upperRow = table.child(upper);
      const lowerRow = table.child(upper + 1);

      // Keep the caret at the same spot inside the row it travelled with.
      const caret = state.selection.from;
      const movedCaret =
        direction < 0
          ? caret - upperRow.nodeSize
          : caret + lowerRow.nodeSize;

      return editor
        .chain()
        .command(({ tr }) => {
          tr.replaceWith(
            upperStart,
            upperStart + upperRow.nodeSize + lowerRow.nodeSize,
            [lowerRow, upperRow]
          );
          tr.setSelection(Selection.near(tr.doc.resolve(movedCaret)));
          return true;
        })
        .scrollIntoView()
        .run();
    };

    const tableFromRuler = () => {
      const { state } = editor;
      const { schema, selection } = state;
      const { $from } = selection;
      const types = schema.nodes;
      if (!selection.empty || !types.table) return false;

      const block = $from.parent;
      if (
        block.type.name !== "paragraph" ||
        $from.parentOffset !== block.content.size ||
        !RULER.test(block.textContent)
      ) {
        return false;
      }

      const dashes = block.textContent
        .slice(1, -1)
        .split("+")
        .map((run) => run.length);
      const total = dashes.reduce((sum, n) => sum + n, 0);
      // One decimal place; the last column takes the rounding slack so the
      // split always adds up to exactly 100%.
      const widths = dashes.map((n) => Math.round((n / total) * 1000) / 10);
      widths[widths.length - 1] = Math.round(
        (100 - widths.slice(0, -1).reduce((sum, n) => sum + n, 0)) * 10
      ) / 10;

      // `style` and `border` exist only when TableAttributes is loaded (the
      // document presets); without it the table is still built, unstyled.
      // Widths go on the cells as `width: %` - see PlainTable in presets.js.
      const styled = (type, attrs) =>
        Object.keys(attrs).reduce((acc, key) => {
          if (key in type.attrs) acc[key] = attrs[key];
          return acc;
        }, {});
      const cells = widths.map((width) =>
        types.tableCell.createAndFill(
          styled(types.tableCell, { style: `width: ${width}%` })
        )
      );
      const tableNode = types.table.create(
        styled(types.table, { border: "1", style: "width: 100%" }),
        types.tableRow.create(null, cells)
      );

      const from = $from.before();
      // A table as the last block leaves nowhere to type after it.
      const isLast = $from.indexAfter($from.depth - 1) === $from.node(-1).childCount;
      const content = isLast
        ? [tableNode, types.paragraph.create()]
        : [tableNode];

      return editor
        .chain()
        .command(({ tr }) => {
          tr.replaceWith(from, $from.after(), content);
          // table > row > cell > paragraph: the first cell's text starts 4 in.
          tr.setSelection(Selection.near(tr.doc.resolve(from + 4)));
          return true;
        })
        .scrollIntoView()
        .run();
    };

    return {
      "Mod-s": () => {
        if (this.options.onSave) this.options.onSave(editor.getHTML());
        return true;
      },
      "Mod-p": () => {
        if (!this.options.onPrint) return false;
        return this.options.onPrint(editor.getHTML()) !== false;
      },

      "Mod-l": align("left"),
      "Mod-e": align("center"),
      "Mod-r": align("right"),
      "Mod-j": align("justify"),
      "Mod-m": indent(true),
      // Word's counterpart to Ctrl+M.
      "Mod-Shift-m": indent(false),

      "Alt-Home": goToCell((rect) => [rect.top, 0]),
      "Alt-End": goToCell((rect, map) => [rect.top, map.width - 1]),
      "Alt-PageUp": goToCell((rect) => [0, rect.left]),
      "Alt-PageDown": goToCell((rect, map) => [map.height - 1, rect.left]),
      "Alt-Shift-ArrowUp": moveRow(-1),
      "Alt-Shift-ArrowDown": moveRow(1),

      Enter: tableFromRuler,
    };
  },
});

export default WordShortcuts;
